import {
  BulkCreateResponse,
  BulkValidationResponse,
  DuplicatedEmail,
} from "../../../../services/schools/school.types";

export interface DuplicateConflictSplit {
  /** Matches inside THIS school — the only ones the backend will update. */
  updatable: DuplicatedEmail[];
  /** Matches in another school — the backend refuses to create or update them. */
  blocked: DuplicatedEmail[];
}

/**
 * SCRUM-822: the duplicate report spans every school, but only a match inside
 * the school being edited can be updated. A foreign-school match is a hard
 * block server-side, so the modal must never count it as updatable — otherwise
 * "Actualizar Existentes (3)" promises work the backend will reject.
 */
export const splitDuplicateConflicts = (
  validationResult: BulkValidationResponse | null
): DuplicateConflictSplit => {
  const conflicts = validationResult?.duplicatedEmails ?? [];
  return {
    updatable: conflicts.filter((conflict) => conflict.sameSchool),
    blocked: conflicts.filter((conflict) => !conflict.sameSchool),
  };
};

export interface UpdatedBulkUser {
  id: number;
  name: string;
  email: string;
}

export interface BulkOutcome {
  createdCount: number;
  failedCount: number;
  updated: UpdatedBulkUser[];
  requiresConfirmation: boolean;
}

/**
 * The upload response mixes creations and updates in one `results` array. An
 * update silently rewrote an existing user's grades and classes, so the UI has
 * to report those apart from the plain creations.
 */
export const buildBulkOutcome = (
  result: BulkCreateResponse | null
): BulkOutcome => {
  if (!result) {
    return {
      createdCount: 0,
      failedCount: 0,
      updated: [],
      requiresConfirmation: false,
    };
  }

  const rows = result.results ?? [];
  const updatedRows = rows.filter(
    (row) => row.success && row.operation === "updated" && row.user
  );

  return {
    createdCount: rows.filter(
      (row) => row.success && row.operation === "created"
    ).length,
    failedCount: result.failed ?? 0,
    updated: updatedRows.map((row) => ({
      id: row.user!.id,
      name: `${row.user!.first_name} ${row.user!.last_name}`.trim(),
      email: row.user!.email,
    })),
    requiresConfirmation: result.requiresConfirmation === true,
  };
};

/**
 * La UI está en español y estos textos los lee un humano, así que la
 * concordancia importa: "1 usuario", no "1 usuarios".
 */
const plural = (count: number, singular: string, many: string): string =>
  `${count} ${count === 1 ? singular : many}`;

export const countUsers = (count: number): string =>
  plural(count, "usuario", "usuarios");

const countEmails = (count: number): string =>
  plural(count, "correo", "correos");

/**
 * SCRUM-822: el resumen de validación se pintaba con `summary.message`, que el
 * backend arma en inglés. El copy es responsabilidad del front; del backend
 * sólo salen los números.
 */
export const buildValidationSummaryText = (
  validationResult: BulkValidationResponse | null
): string => {
  if (!validationResult) return "";

  const { total, duplicated, valid } = validationResult.summary;
  // `updatable`/`foreignSchool` los agregó SCRUM-822: si el backend todavía no
  // los manda, todo duplicado se asume de esta escuela.
  const updatable = validationResult.summary.updatable ?? duplicated;
  const foreign = validationResult.summary.foreignSchool ?? 0;

  const parts: string[] = [];
  if (valid > 0) {
    parts.push(`${countUsers(valid)} ${valid === 1 ? "se creará" : "se crearán"}`);
  }
  if (updatable > 0) {
    parts.push(
      `${countUsers(updatable)} ya ${
        updatable === 1 ? "existe" : "existen"
      } en esta escuela y se ${
        updatable === 1 ? "actualizará" : "actualizarán"
      }`
    );
  }
  if (foreign > 0) {
    parts.push(
      `${countUsers(foreign)} ${
        foreign === 1 ? "pertenece" : "pertenecen"
      } a otra escuela y se ${foreign === 1 ? "omitirá" : "omitirán"}`
    );
  }

  if (parts.length === 0) {
    return `No hay usuarios para procesar (${countUsers(total)} en el archivo).`;
  }

  return `${parts.join(", ")}.`;
};

/** Aviso sobre la tabla de conflictos. */
export const buildConflictNotice = (
  updatable: number,
  blocked: number
): string => {
  const head = `${countUsers(updatable)} de esta escuela se ${
    updatable === 1 ? "actualizará" : "actualizarán"
  } con los datos del archivo.`;
  if (blocked === 0) return head;
  return `${head} ${countEmails(blocked)} ${
    blocked === 1 ? "pertenece" : "pertenecen"
  } a otra escuela y se ${blocked === 1 ? "omitirá" : "omitirán"}.`;
};

/**
 * SCRUM-916: encabezados aceptados para la columna de plantel. Case-insensitive
 * y sin acentos, igual que el intérprete del backend (R6.5).
 */
const CAMPUS_COLUMN_KEYS = ["plantel", "campus", "sede"];

const normalizeKey = (key: string): string =>
  key
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Lee la celda de plantel de una fila del CSV, venga con el header que venga. */
export const readRowCampus = (row: Record<string, any>): string => {
  const entry = Object.keys(row).find((key) =>
    CAMPUS_COLUMN_KEYS.includes(normalizeKey(key))
  );
  const raw = entry ? row[entry] : undefined;
  return typeof raw === "string" ? raw.trim() : "";
};

const splitList = (value: any): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

/**
 * Normaliza una fila cruda del CSV (o de Google Sheets: los dos caminos
 * parsean con Papa y comparten esta función, así una columna nueva no puede
 * quedar soportada en uno solo).
 */
export const parseBulkRow = (row: Record<string, any>) => ({
  ...row,
  grades: Array.isArray(row.grades)
    ? row.grades
    : splitList(row.grades).map((grade) => parseInt(grade, 10)),
  classroom_codes: splitList(row.classroom_codes),
  campus: readRowCampus(row),
});

export interface CampusOptionRef {
  id: number;
  name: string;
  abbreviation: string | null;
}

export interface ResolvedRowCampus {
  campusId?: number;
  error?: string;
}

/**
 * Resuelve el plantel de una fila: primero lo que trae la fila (abreviatura o
 * nombre, case-insensitive), y si no trae nada, el plantel elegido en pantalla.
 * En una escuela multi-plantel sin ninguno de los dos NO se adivina: la fila se
 * marca con un error explícito (R6.3).
 */
export const resolveRowCampus = (
  rawCampus: string,
  campuses: CampusOptionRef[],
  selectedCampusId?: number
): ResolvedRowCampus => {
  if (campuses.length <= 1) return {};

  const value = (rawCampus ?? "").trim();
  if (value) {
    const match = campuses.find(
      (campus) =>
        campus.name.trim().toLowerCase() === value.toLowerCase() ||
        (campus.abbreviation ?? "").trim().toLowerCase() ===
          value.toLowerCase()
    );
    if (!match) {
      return { error: `Plantel no encontrado: ${value}` };
    }
    return { campusId: match.id };
  }

  if (selectedCampusId) return { campusId: selectedCampusId };

  return {
    error:
      "Falta la columna Plantel: elegí un plantel destino o agregá la columna al archivo",
  };
};

/**
 * Agrupa las filas por plantel destino. `POST /schools/users/bulk` recibe UN
 * `school_id`, y un plantel ES una fila de School: cada grupo se sube contra su
 * propio plantel. Sin planteles hay un solo grupo, idéntico al de siempre.
 */
export const groupUsersByCampus = <T extends { campus_id?: number }>(
  users: T[],
  fallbackSchoolId: number
): Map<number, T[]> => {
  const groups = new Map<number, T[]>();
  for (const user of users) {
    const key = user.campus_id ?? fallbackSchoolId;
    const bucket = groups.get(key);
    if (bucket) bucket.push(user);
    else groups.set(key, [user]);
  }
  return groups;
};

/**
 * Une las validaciones de cada grupo en una sola respuesta: la UI de conflictos
 * muestra un único resumen, y con un solo grupo esto es la identidad.
 */
export const mergeValidationResults = (
  results: (BulkValidationResponse | null)[]
): BulkValidationResponse | null => {
  const present = results.filter(
    (result): result is BulkValidationResponse => result !== null
  );
  if (present.length === 0) return null;
  if (present.length === 1) return present[0];

  const duplicatedEmails = present.flatMap(
    (result) => result.duplicatedEmails ?? []
  );
  const sum = (pick: (r: BulkValidationResponse) => number | undefined) =>
    present.reduce((total, result) => total + (pick(result) ?? 0), 0);

  return {
    hasConflicts: present.some((result) => result.hasConflicts),
    duplicatedEmails,
    validUsers: present.flatMap((result) => result.validUsers ?? []),
    summary: {
      total: sum((result) => result.summary.total),
      duplicated: sum((result) => result.summary.duplicated),
      valid: sum((result) => result.summary.valid),
      updatable: sum((result) => result.summary.updatable),
      foreignSchool: sum((result) => result.summary.foreignSchool),
      message: "",
    },
  };
};
